import { useCallback } from 'react';

import { type UploadAnalyticsContext, useTrackUploadAborted } from '@air/analytics';
import { isLarge } from '@air/redux-uploader';
import { type UseDeleteUploadParams } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import {
  getPrivateUploadAnalyticsParams,
  getUploadAbortedAnalyticsParams,
} from '~/components/Upload/utils/uploadAnalytics';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useAirStore } from '~/utils/ReduxUtils';
import { sendAbortPrivateMultipartUpload } from '~/utils/uploadApi/privateUploadApi/privateLargeUploadApi';

export type UsePrivateUploadDeleteParams = {
  analyticsContext: UploadAnalyticsContext;
};

export const usePrivateUploadDelete = ({ analyticsContext }: UsePrivateUploadDeleteParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const store = useAirStore();
  const { trackUploadAborted } = useTrackUploadAborted();

  const onDeleteUpload = useCallback<NonNullable<UseDeleteUploadParams['onDelete']>>(
    async (upload) => {
      if (!workspaceId) {
        return;
      }

      if (isLarge(upload)) {
        try {
          const analyticsParams = getPrivateUploadAnalyticsParams({
            context: analyticsContext,
            state: store.getState(),
            upload,
          });

          trackUploadAborted(getUploadAbortedAnalyticsParams({ commonParams: analyticsParams, upload }));
        } catch (analyticsError) {
          reportErrorToBugsnag({ error: analyticsError, context: 'Failed to track upload aborted analytics' });
        }
      }

      if (isLarge(upload) && upload.s3Info.uploadUrlInfo) {
        void sendAbortPrivateMultipartUpload({ uploadUrlInfo: upload.s3Info.uploadUrlInfo, workspaceId });
      }
    },
    [analyticsContext, store, trackUploadAborted, workspaceId],
  );

  return { onDeleteUpload };
};
